import React from 'react';
import { Box, Checkbox, FormControl, FormLabel, HStack, Input, Text } from '@chakra-ui/react';

// Weekly business hours editor shared by the chat handoff page and the company Settings card
// (scenario gap G1, 2026-10-26). Form shape: { anyTime, timezone, days: { mon: { on, start, end }, ... } }.
export const DAYS = [
  ['mon', '周一', 'Mon'], ['tue', '周二', 'Tue'], ['wed', '周三', 'Wed'], ['thu', '周四', 'Thu'],
  ['fri', '周五', 'Fri'], ['sat', '周六', 'Sat'], ['sun', '周日', 'Sun'],
];
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
export const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const browserZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

// Server business_hours (null = any time) → form fields.
export const hoursToFormFields = (hours, zone = browserZone()) => ({
  anyTime: !hours,
  timezone: hours?.timezone || zone,
  days: Object.fromEntries(DAYS.map(([key]) => {
    const day = hours?.weekly?.[key];
    return [key, day ? { on: true, start: day.start, end: day.end } : { on: !hours && WEEKDAYS.includes(key), start: '09:00', end: '18:00' }];
  })),
});

// Form fields → business_hours; undefined when invalid, null for "any time".
export const formFieldsToHours = (form) => {
  if (form.anyTime) return null;
  const weekly = {};
  for (const [key] of DAYS) {
    const day = form.days[key];
    if (day?.on) {
      if (!HHMM.test(day.start) || !HHMM.test(day.end) || day.start === day.end) return undefined;
      weekly[key] = { start: day.start, end: day.end };
    }
  }
  if (!String(form.timezone || '').trim()) return undefined;
  return { timezone: form.timezone.trim(), weekly };
};

export default function BusinessHoursEditor({ form, onChange, lang = 'zh', anyTimeLabel }) {
  const zh = lang === 'zh';
  const setDay = (key, patch) => onChange({ ...form, days: { ...form.days, [key]: { ...form.days[key], ...patch } } });
  return (
    <Box>
      <Checkbox mb={2} isChecked={form.anyTime} onChange={(e) => onChange({ ...form, anyTime: e.target.checked })}>
        {anyTimeLabel || (zh ? '任何时间' : 'Any time')}
      </Checkbox>
      {!form.anyTime && (
        <Box mb={3}>
          <FormControl mb={2} maxW="320px">
            <FormLabel fontSize="sm">{zh ? '时区' : 'Time zone'}</FormLabel>
            <Input size="sm" value={form.timezone} placeholder="America/Los_Angeles" onChange={(e) => onChange({ ...form, timezone: e.target.value })} />
          </FormControl>
          {DAYS.map(([key, zhName, enName]) => (
            <HStack key={key} spacing={3} mb={1}>
              <Checkbox minW="72px" isChecked={form.days[key].on} onChange={(e) => setDay(key, { on: e.target.checked })}>{zh ? zhName : enName}</Checkbox>
              <Input size="sm" type="time" maxW="120px" value={form.days[key].start} isDisabled={!form.days[key].on}
                aria-label={`${zh ? zhName : enName} ${zh ? '开始' : 'start'}`} onChange={(e) => setDay(key, { start: e.target.value })} />
              <Text fontSize="sm">–</Text>
              <Input size="sm" type="time" maxW="120px" value={form.days[key].end} isDisabled={!form.days[key].on}
                aria-label={`${zh ? zhName : enName} ${zh ? '结束' : 'end'}`} onChange={(e) => setDay(key, { end: e.target.value })} />
            </HStack>
          ))}
        </Box>
      )}
    </Box>
  );
}
